'use client'

import { uuidv4 } from 'uuidv7'
import {
  ConsultationQuestionnaire,
  ConsultationQuestionnaireType,
  ConsultationQuestionnaireValidator,
} from '@/lib/questionnaires/GeneralConsultation'
import { DefaultIHIVerificationState } from '@/shared/request/defaults'
import { Session } from 'next-auth'
import {
  AvailableScheduleResponse,
  PatientProfile,
  ProfileStatus,
} from '@/lib/apis/types'
import { SupportingDocument } from '@/features/prescriptions/SupportingDocumentItem'
import { IHIVerificationState } from '@/shared/components/hi/HealthcareIdentifierForm'
import { PersonalDetailsFormState } from '@/shared/request/PersonalDetailsFormStage'
import { createStore } from 'zustand'
import { computeProfileFormState } from '@/shared/request/utils'
import { createContext, PropsWithChildren, useRef } from 'react'
import { DocumentUploadFormContext } from '@/shared/request/DocumentUploadFormContext'
import { IHIVerifyFormContext } from '@/shared/request/IHIVerifyFormContext'
import { PersonalDetailsFormContext } from '@/shared/request/PersonalDetailsFormContext'
import {
  AppointmentDate,
  CallArrangementContext,
  SelectedSchedule,
} from '@/features/common-requests/call-arrangement-context'
import { computeAppointmentDates } from '@/lib/utils'

type ConsultationRequestStageType =
  | 'PERSONAL-INFORMATION'
  | 'IHI-VERIFICATION'
  | 'QUESTIONNAIRE'
  | 'UPLOAD-DOCUMENTS'
  | 'CALL-ARRANGEMENT'
  | 'REVIEW'

export type ConsultationRequestStage =
  | {
      type: Exclude<ConsultationRequestStageType, 'QUESTIONNAIRE'>
      title: string
    }
  | { type: 'QUESTIONNAIRE'; title: string; page: number }

export interface ConsultationRequestFormProps {
  session: Session | null

  initialProfile: PatientProfile | null
  profileStatus: ProfileStatus | null
  availableSlots: AvailableScheduleResponse
}

const DefaultProps = {
  requestId: uuidv4(),
  currentStageIdx: 0,
  questionnaireForm: {
    values: ConsultationQuestionnaireValidator.cast({}),
    touched: new Set<string>(),
  },
  supportingDocuments: [],
  ihiVerificationState: DefaultIHIVerificationState,
  verifiedIdentifierAttachmentPending: false,
  verifiedIdentifierAttached: false,
}

interface ConsultationRequestFormState extends ConsultationRequestFormProps {
  requestId: string

  stages: Array<ConsultationRequestStage>
  currentStageIdx: number

  // Call arrangement form related props
  availableDates: Array<AppointmentDate>
  selectedSchedule: SelectedSchedule | null
  onSelectSchedule: (selected: SelectedSchedule | null) => void
  immediateSlotAvailable: boolean

  questionnaireForm: {
    values: ConsultationQuestionnaireType
    touched: Set<string>
  }

  updateLoginState: (
    session: Session | null,
    profile: PatientProfile | null,
    profileStatus: ProfileStatus | null,
  ) => void
  reset: (availableSlots: AvailableScheduleResponse) => void

  onNext: () => void
  onPrev: () => void
  goToStage: (type: ConsultationRequestStageType, page?: number) => void

  // Questionnaire section
  updateQuestionnaireFormValues: (values: ConsultationQuestionnaireType) => void

  // Document upload section
  supportingDocuments: Array<SupportingDocument>
  addSupportingDocument: (document: SupportingDocument) => void
  removeSupportingDocument: (name: string) => void
  updateSupportingDocument: (
    name: string,
    updater: () => Partial<SupportingDocument>,
  ) => void

  // IHI validation section
  ihiVerificationState: IHIVerificationState
  verifiedIdentifierAttachmentPending: boolean
  verifiedIdentifierAttached: boolean
  updateIHIVerificationState: (state: IHIVerificationState) => void
  setVerifiedIdentifierAttachmentPending: (pending: boolean) => void
  completeVerifiedIdentifierAttachment: (
    profileStatus: ProfileStatus | null,
  ) => void
  returnToIdentifierCorrection: () => void

  // Personal information section
  profileFormState: PersonalDetailsFormState
  updateProfileFormState: (
    updater: (state: PersonalDetailsFormState) => PersonalDetailsFormState,
  ) => void
}

export const createConsultationRequestFormStore = (
  initProps: ConsultationRequestFormProps,
) => {
  const { session, initialProfile, profileStatus, availableSlots } = initProps

  const stages = computeStages(session, profileStatus)
  const profileFormState = computeProfileFormState(
    initialProfile,
    profileStatus,
  )

  return createStore<ConsultationRequestFormState>()((set) => ({
    ...DefaultProps,
    ...initProps,
    availableDates: computeAppointmentDates(availableSlots),
    selectedSchedule: availableSlots.immediateSlotAvailable
      ? { type: 'immediate' }
      : null,
    immediateSlotAvailable: availableSlots.immediateSlotAvailable === true,

    stages,

    profileFormState,

    updateLoginState: (
      session: Session | null,
      profile: PatientProfile | null,
      profileStatus: ProfileStatus | null,
    ) =>
      set((state) => {
        const effectiveProfileStatus =
          state.verifiedIdentifierAttached &&
          profileStatus &&
          !profileStatus.ihiSubmitted
            ? { ...profileStatus, ihiSubmitted: true }
            : profileStatus
        const newStages = computeStages(session, effectiveProfileStatus)

        const previousStageType = state.stages[state.currentStageIdx].type
        let newStageIdx = state.currentStageIdx
        const shouldAttachVerifiedIdentifier =
          previousStageType === 'PERSONAL-INFORMATION' &&
          session !== null &&
          !effectiveProfileStatus?.ihiSubmitted &&
          Boolean(state.ihiVerificationState.verified)
        const keepPersonalInformationForIdentifierAttachment =
          previousStageType === 'PERSONAL-INFORMATION' &&
          session !== null &&
          (state.verifiedIdentifierAttachmentPending ||
            shouldAttachVerifiedIdentifier)
        const requiresIdentifierCorrection =
          previousStageType === 'PERSONAL-INFORMATION' &&
          session !== null &&
          !effectiveProfileStatus?.ihiSubmitted &&
          !shouldAttachVerifiedIdentifier &&
          !state.verifiedIdentifierAttachmentPending

        if (keepPersonalInformationForIdentifierAttachment) {
          newStageIdx = state.currentStageIdx
        } else if (requiresIdentifierCorrection) {
          const identifierStageIdx = newStages.findIndex(
            (stage) => stage.type === 'IHI-VERIFICATION',
          )
          if (identifierStageIdx >= 0) newStageIdx = identifierStageIdx
        } else if (
          previousStageType === 'IHI-VERIFICATION' &&
          effectiveProfileStatus?.ihiSubmitted
        ) {
          newStageIdx = findNextRemainingStageIndex(
            state.stages,
            newStages,
            state.currentStageIdx,
          )
        } else if (previousStageType === 'PERSONAL-INFORMATION') {
          const personalInformationStageIdx = newStages.findIndex(
            (stage) => stage.type === 'PERSONAL-INFORMATION',
          )
          newStageIdx =
            personalInformationStageIdx >= 0
              ? personalInformationStageIdx
              : findNextRemainingStageIndex(
                  state.stages,
                  newStages,
                  state.currentStageIdx,
                )
        }

        let ihiVerificationState = state.ihiVerificationState
        if (
          effectiveProfileStatus?.ihiSubmitted &&
          !keepPersonalInformationForIdentifierAttachment
        ) {
          // If ihi has already been submitted, reset the ihi state so that the submitter has the correct information
          // that we don't need to send the ihi information.
          ihiVerificationState = DefaultIHIVerificationState
        }

        const refreshedProfileFormState = computeProfileFormState(
          profile,
          profileStatus,
        )
        const profileFormState =
          previousStageType === 'PERSONAL-INFORMATION'
            ? {
                values: state.profileFormState.values,
                verification: refreshedProfileFormState.verification,
              }
            : refreshedProfileFormState

        return {
          currentStageIdx: newStageIdx,
          stages: keepPersonalInformationForIdentifierAttachment
            ? state.stages
            : newStages,
          profileFormState,
          initialProfile: profile,
          ihiVerificationState,
          profileStatus: effectiveProfileStatus,
          session,
          verifiedIdentifierAttached:
            state.verifiedIdentifierAttached ||
            Boolean(effectiveProfileStatus?.ihiSubmitted),
        }
      }),

    reset: (availableSlots: AvailableScheduleResponse) =>
      set({
        ...DefaultProps,
        ...initProps,

        availableDates: computeAppointmentDates(availableSlots),
        selectedSchedule: availableSlots.immediateSlotAvailable
          ? { type: 'immediate' }
          : null,
        immediateSlotAvailable: availableSlots.immediateSlotAvailable === true,
      }),

    onNext: () =>
      set((state) => ({ currentStageIdx: state.currentStageIdx + 1 })),
    onPrev: () =>
      set((state) => ({ currentStageIdx: state.currentStageIdx - 1 })),
    goToStage: (type: ConsultationRequestStageType, page?: number) =>
      set((state) => {
        const currentStageIdx = state.stages.findIndex(
          (stage) =>
            stage.type === type &&
            (stage.type !== 'QUESTIONNAIRE' ||
              page === undefined ||
              stage.page === page),
        )
        return currentStageIdx >= 0 ? { currentStageIdx } : {}
      }),

    updateQuestionnaireFormValues: (values: ConsultationQuestionnaireType) =>
      set((state) => ({
        questionnaireForm: {
          values,
          touched: state.questionnaireForm.touched,
        },
      })),

    addSupportingDocument: (document: SupportingDocument) =>
      set((state) => ({
        supportingDocuments: [...state.supportingDocuments, document],
      })),
    removeSupportingDocument: (name: string) =>
      set((state) => ({
        supportingDocuments: state.supportingDocuments.filter(
          (doc) => doc.name !== name,
        ),
      })),
    updateSupportingDocument: (
      name: string,
      updater: () => Partial<SupportingDocument>,
    ) =>
      set((state) => ({
        supportingDocuments: state.supportingDocuments.map((doc) => {
          if (doc.name === name) return { ...doc, ...updater() }
          else return doc
        }),
      })),

    updateIHIVerificationState: (state: IHIVerificationState) =>
      set(() => ({ ihiVerificationState: state })),

    setVerifiedIdentifierAttachmentPending: (pending: boolean) =>
      set({ verifiedIdentifierAttachmentPending: pending }),

    completeVerifiedIdentifierAttachment: (
      refreshedProfileStatus: ProfileStatus | null,
    ) =>
      set((state) => {
        const nextProfileStatus = refreshedProfileStatus
          ? { ...refreshedProfileStatus, ihiSubmitted: true }
          : state.profileStatus
            ? { ...state.profileStatus, ihiSubmitted: true }
            : null
        const nextStages = computeStages(state.session, nextProfileStatus)
        const previousStage = state.stages[state.currentStageIdx]
        const matchingStageIdx = nextStages.findIndex(
          (stage) =>
            stage.type === previousStage.type &&
            (stage.type !== 'QUESTIONNAIRE' ||
              previousStage.type !== 'QUESTIONNAIRE' ||
              stage.page === previousStage.page),
        )

        return {
          currentStageIdx:
            matchingStageIdx >= 0
              ? matchingStageIdx
              : findNextRemainingStageIndex(
                  state.stages,
                  nextStages,
                  state.currentStageIdx,
                ),
          stages: nextStages,
          profileStatus: nextProfileStatus,
          ihiVerificationState: DefaultIHIVerificationState,
          verifiedIdentifierAttachmentPending: false,
          verifiedIdentifierAttached: true,
        }
      }),

    returnToIdentifierCorrection: () =>
      set((state) => {
        const identifierStageIdx = state.stages.findIndex(
          (stage) => stage.type === 'IHI-VERIFICATION',
        )
        return {
          currentStageIdx:
            identifierStageIdx >= 0
              ? identifierStageIdx
              : state.currentStageIdx,
          ihiVerificationState: {
            ...state.ihiVerificationState,
            verified: undefined,
          },
          verifiedIdentifierAttachmentPending: false,
          verifiedIdentifierAttached: false,
        }
      }),

    updateProfileFormState: (
      updater: (state: PersonalDetailsFormState) => PersonalDetailsFormState,
    ) =>
      set((state) => ({
        profileFormState: updater(state.profileFormState),
      })),

    onSelectSchedule: (selected: SelectedSchedule | null) => {
      set(() => ({ selectedSchedule: selected }))
    },
  }))
}

type ConsultationRequestFormStore = ReturnType<
  typeof createConsultationRequestFormStore
>

function computeStages(
  session: Session | null,
  profileStatus: ProfileStatus | null,
): Array<ConsultationRequestStage> {
  const stages: Array<ConsultationRequestStage> = [
    ...ConsultationQuestionnaire.pages.map((_, page) => ({
      type: 'QUESTIONNAIRE' as const,
      title: 'Digital Consultation',
      page,
    })),
    { type: 'UPLOAD-DOCUMENTS' as const, title: 'Supporting Documents' },
  ]
  if (!profileStatus?.ihiSubmitted)
    stages.push({
      type: 'IHI-VERIFICATION' as const,
      title: 'Enter Medicare Details',
    })
  if (!session || !profileStatus?.completed || !profileStatus.mobileVerified)
    stages.push({
      type: 'PERSONAL-INFORMATION' as const,
      title: 'Add Your Details',
    })

  stages.push({
    type: 'CALL-ARRANGEMENT' as const,
    title: 'When do you want to be called?',
  })
  stages.push({ type: 'REVIEW' as const, title: 'Review Your Request' })
  return stages
}

function findNextRemainingStageIndex(
  previousStages: Array<ConsultationRequestStage>,
  nextStages: Array<ConsultationRequestStage>,
  previousStageIdx: number,
): number {
  for (
    let index = previousStageIdx + 1;
    index < previousStages.length;
    index++
  ) {
    const nextStageIdx = nextStages.findIndex(
      (stage) => stage.type === previousStages[index].type,
    )
    if (nextStageIdx >= 0) return nextStageIdx
  }

  return Math.max(0, nextStages.length - 1)
}

export const ConsultationRequestFormContext =
  createContext<ConsultationRequestFormStore | null>(null)

export default function ConsultationRequestFormProvider({
  children,
  ...props
}: PropsWithChildren<ConsultationRequestFormProps>) {
  const store = useRef<ConsultationRequestFormStore | null>(null)

  if (!store.current) {
    store.current = createConsultationRequestFormStore(props)
  }

  return (
    <ConsultationRequestFormContext.Provider value={store.current}>
      <DocumentUploadFormContext.Provider value={store.current}>
        <IHIVerifyFormContext.Provider value={store.current}>
          <PersonalDetailsFormContext.Provider value={store.current}>
            <CallArrangementContext.Provider value={store.current}>
              {children}
            </CallArrangementContext.Provider>
          </PersonalDetailsFormContext.Provider>
        </IHIVerifyFormContext.Provider>
      </DocumentUploadFormContext.Provider>
    </ConsultationRequestFormContext.Provider>
  )
}
